/**
 * Mobile bottom bar (sections/mobile-bottom-bar.liquid)
 * <mobile-bottom-bar>
 *  - Highlights the item whose data-active-path is in the current URL
 *  - Slides away while scrolling down, back when scrolling up (live behaviour)
 *  - Hides while a theme drawer / modal is open (body "*--open" or "overflow-hidden" classes)
 *  - Search item opens the theme search modal (falls back to the search page)
 *  - Chat circle opens the configured provider (falls back to a link):
 *      Sagepilot     -> loads app.sagepilot.ai/chat-widget.js when the page is idle, then
 *                       window.ChatWidget.open() (official API). Native launcher hidden per setting.
 *      Shopify Inbox -> <shopify-chat>.show() (the element's own method). Inbox stays hidden (static
 *                       <style> in the section) until our button adds .mobile-bottom-bar-chat-open to
 *                       <html>, and is hidden again when the chat closes. Its launcher is also hidden
 *                       inside its shadow roots for the whole visit. Older builds: clicks the launcher.
 *      Custom        -> 'theme:support-chat:open' event (below).
 *
 * Custom support chat integration contract (any future provider):
 *   document.addEventListener('theme:support-chat:open', (event) => {
 *     event.preventDefault();          // tell the bar the chat was opened
 *     window.MyChatWidget.open();      // provider specific
 *   });
 *   If nobody calls preventDefault(), the fallback link opens.
 */
(() => {
  if (customElements.get('mobile-bottom-bar')) return;

  const SCROLL_DELTA = 6;
  const SUPPORT_CHAT_EVENT = 'theme:support-chat:open';
  const MOBILE_MEDIA = '(max-width: 950px)';
  const OPEN_RETRY_MS = 150;
  const OPEN_TIMEOUT_MS = 8000;
  const GREETING_KEY = 'theme:mobile-bottom-bar:greeting-seen';
  const GREETING_VISIBLE_MS = 10000;
  const CHAT_OPEN_CLASS = 'mobile-bottom-bar-chat-open'; // on <html>, see the <style> in the section

  /** Adds a <style> once (by id) to document.head or a shadow root. */
  const injectStyle = (root, id, css) => {
    if (root.querySelector?.(`#${id}`)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = css;
    (root === document ? document.head : root).appendChild(style);
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /**
   * Welcome sound for the greeting card. Browsers only allow sound after the shopper has touched,
   * clicked or typed on the page, so the sound is unlocked on the first interaction and the
   * greeting plays it only when that has happened (otherwise it stays silent - never an error).
   * Default: a soft two-note chime made in the browser (no file to load). Optional: a sound file URL.
   */
  const Chime = {
    /** @type {AudioContext | null} */
    context: null,
    unlocked: false,

    /** @param {AbortSignal} signal */
    armUnlock(signal) {
      const unlock = () => {
        this.unlocked = true;
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;
        this.context ||= new AudioContextClass();
        if (this.context.state === 'suspended') this.context.resume().catch(() => {});
      };
      for (const type of ['pointerdown', 'keydown', 'touchend']) {
        document.addEventListener(type, unlock, { once: true, capture: true, passive: true, signal });
      }
    },

    /**
     * @param {number} volume - 0..1
     * @param {string} [url] - optional sound file (e.g. uploaded to Content > Files)
     */
    play(volume, url) {
      if (!this.unlocked) return;
      if (url) {
        const audio = new Audio(url);
        audio.volume = Math.min(1, Math.max(0, volume));
        audio.play().catch(() => {});
        return;
      }
      const context = this.context;
      if (!context || context.state !== 'running') return;

      const start = context.currentTime + 0.02;
      // A5 then E6: short, bright "ding-ding" with a soft attack and fade
      [[880, 0], [1318.5, 0.12]].forEach(([frequency, offset]) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = frequency;
        gain.gain.setValueAtTime(0.0001, start + offset);
        gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, 0.25 * volume), start + offset + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.45);
        oscillator.connect(gain).connect(context.destination);
        oscillator.start(start + offset);
        oscillator.stop(start + offset + 0.5);
      });
    }
  };

  /**
   * Shopify Inbox (no official API). Its widget is a custom element whose launcher sits inside
   * one or more shadow roots, so page CSS can't reach it.
   *  - 2026+:  <shopify-chat> ... <button class="activator">
   *  - older:  <inbox-online-store-chat id="ShopifyChat"> ... .chat-app > button
   */
  const ShopifyInbox = {
    hosts: 'shopify-chat, inbox-online-store-chat, #ShopifyChat, #shopify-chat',
    launchers: 'button.activator, .chat-app > button',

    /**
     * Depth-first search through a root and every nested shadow root.
     * @param {Document | ShadowRoot | Element} root
     * @param {number} [depth]
     * @returns {HTMLElement | null}
     */
    findIn(root, depth = 0) {
      const direct = root.querySelector?.(this.launchers);
      if (direct) return direct;
      if (depth > 6) return null;
      for (const element of root.querySelectorAll?.('*') ?? []) {
        if (!element.shadowRoot) continue;
        const found = this.findIn(element.shadowRoot, depth + 1);
        if (found) return found;
      }
      return null;
    },

    /**
     * Every root (document or shadow root) that currently holds an Inbox launcher. Inbox can have
     * more than one (the lightweight placeholder and the real widget, or one per rebuild).
     * @param {Document | ShadowRoot | Element} root
     * @param {Set<Document | ShadowRoot>} [found]
     * @param {number} [depth]
     * @returns {Set<Document | ShadowRoot>}
     */
    launcherRoots(root = document, found = new Set(), depth = 0) {
      const launcher = root.querySelector?.(this.launchers);
      if (launcher) found.add(/** @type {Document | ShadowRoot} */ (launcher.getRootNode()));
      if (depth > 6) return found;
      for (const element of root.querySelectorAll?.('*') ?? []) {
        if (element.shadowRoot) this.launcherRoots(element.shadowRoot, found, depth + 1);
      }
      return found;
    },

    /** @returns {HTMLElement | null} */
    findLauncher() {
      for (const host of document.querySelectorAll(this.hosts)) {
        const found = (host.shadowRoot && this.findIn(host.shadowRoot)) || this.findIn(host);
        if (found) return found;
      }
      return null;
    },

    /**
     * Inbox's launcher is a toggle. After the shopper closes the chat from inside the chat window,
     * the launcher can still report "open" (aria-expanded / aria-pressed = true), so a single click
     * would close it instead of opening it. That is why the chat only opened the first time.
     * @param {HTMLElement} launcher
     */
    isOpen(launcher) {
      return launcher.getAttribute('aria-expanded') === 'true' || launcher.getAttribute('aria-pressed') === 'true';
    },

    /**
     * Hide the launcher by adding a style next to it (inside its shadow root when it has one).
     * @param {string} css - rule(s) using the launcher selectors
     * @returns {boolean} true when the launcher was found
     */
    hideLauncher(css) {
      const roots = this.launcherRoots();
      for (const root of roots) injectStyle(root, 'mobile-bottom-bar-inbox', css);
      return roots.size > 0;
    },

    /** The current Inbox element (2026+: <shopify-chat>, which has show() / close()). */
    host() {
      return document.querySelector('shopify-chat');
    },

    /**
     * Opens the chat. 2026+ Inbox: the element's own show(), which works every time (clicking the
     * launcher stopped working after the first open, because Inbox replaces that button).
     * Inbox may still be loading when the shopper taps, so wait for it briefly.
     * @returns {Promise<boolean>}
     */
    async open() {
      const started = Date.now();
      while (Date.now() - started < OPEN_TIMEOUT_MS) {
        const host = this.host();
        if (host && typeof host.show === 'function') {
          host.show();
          return true;
        }
        if (this.findLauncher()) return this.clickLauncher(); // older Inbox builds (no show())
        await wait(OPEN_RETRY_MS);
      }
      return false;
    },

    /** Older Inbox builds: click the (hidden) launcher, looked up fresh because Inbox re-renders it. */
    async clickLauncher() {
      const launcher = this.findLauncher();
      if (!launcher) return false;
      if (this.isOpen(launcher)) {
        launcher.click(); // reset Inbox's stale "open" state first
        await wait(OPEN_RETRY_MS);
      }
      (this.findLauncher() || launcher).click(); // works while hidden with display: none
      return true;
    }
  };

  /**
   * Sagepilot web chat (official API: window.ChatWidget.init/open/close/isOpen).
   * Loads at most once per page, even if several components ask for it.
   */
  const Sagepilot = {
    /** @type {Promise<void> | null} */
    loading: null,

    isMounted() {
      return Boolean(document.getElementById('chat-widget'));
    },

    /**
     * @param {{ key: string, host: string, hideLauncher: boolean }} config
     * @returns {Promise<void>}
     */
    load(config) {
      if (this.loading) return this.loading;
      this.loading = (async () => {
        if (!window.ChatWidget) {
          if (!config.key) throw new Error('Sagepilot is not loaded and no widget key is set');
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = new URL('/chat-widget.js', config.host).toString();
            script.defer = true;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }
        if (!this.isMounted() && config.key) {
          await window.ChatWidget.init({
            host: config.host,
            key: config.key,
            bottomPadding: 20,
            hideNativeLauncherButton: config.hideLauncher
          });
        }
      })();
      this.loading.catch(() => {
        this.loading = null;
      });
      return this.loading;
    },

    /** open() returns false until the widget has finished mounting, so retry briefly. */
    async open() {
      const started = Date.now();
      while (Date.now() - started < OPEN_TIMEOUT_MS) {
        if (window.ChatWidget?.open?.() === true) return true;
        await wait(OPEN_RETRY_MS);
      }
      return false;
    }
  };

  class MobileBottomBar extends HTMLElement {
    #controller = new AbortController();
    #lastScroll = 0;
    #frame = 0;
    #scrollHidden = false;
    #suppressed = false;
    #inboxCss = '';
    /** @type {MutationObserver | null} */
    #inboxCloseObserver = null;
    /** @type {MutationObserver | null} */
    #bodyObserver = null;

    connectedCallback() {
      const { signal } = this.#controller;

      this.#markActive();
      this.#toggleStoryButton();

      if (this.dataset.hideOnScroll === 'true') {
        this.#lastScroll = window.scrollY;
        window.addEventListener('scroll', this.#onScroll.bind(this), { passive: true, signal });
      }

      this.#bodyObserver = new MutationObserver(this.#onBodyChange.bind(this));
      this.#bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      this.querySelector('[data-bar-search]')?.addEventListener('click', this.#openSearch.bind(this), { signal });
      this.querySelector('[data-bar-chat]')?.addEventListener('click', () => this.#openChat(), { signal });
      this.#setupChatProvider();
      this.#setupGreeting();
      document.addEventListener('story-viewer:ready', this.#toggleStoryButton.bind(this), { signal });

      // Theme editor: re-check when the Story viewer section is added, removed or edited.
      for (const type of ['shopify:section:load', 'shopify:section:unload']) {
        document.addEventListener(type, () => setTimeout(() => this.#toggleStoryButton(), 0), { signal });
      }
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#controller = new AbortController();
      this.#bodyObserver?.disconnect();
      this.#inboxCloseObserver?.disconnect();
      cancelAnimationFrame(this.#frame);
    }

    #markActive() {
      const path = window.location.pathname;
      for (const item of this.querySelectorAll('[data-active-path]')) {
        const match = item.dataset.activePath;
        item.classList.toggle('is-active', Boolean(match) && path.includes(match));
      }
    }

    /**
     * Storefront: hide the story button when there is no Story viewer with stories on the page
     * (it would do nothing). Theme editor: always show it, with a hint when it can't open anything.
     */
    #toggleStoryButton() {
      const story = this.querySelector('.mobile-bottom-bar__story');
      if (!story) return;
      const viewer = document.querySelector('story-viewer');
      const hasStories = Boolean(viewer) && viewer.dataset.count !== '0';
      const inEditor = Boolean(window.Shopify?.designMode);

      story.hidden = !hasStories && !inEditor;
      story.classList.toggle('mobile-bottom-bar__story--inactive', !hasStories);
      const button = story.querySelector('[data-story-open]');
      if (!button) return;
      if (hasStories) {
        button.removeAttribute('title');
      } else {
        button.title = (viewer ? this.dataset.hintNoStories : this.dataset.hintNoViewer) || '';
      }
    }

    #onScroll() {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        const current = Math.max(window.scrollY, 0);
        const delta = current - this.#lastScroll;
        if (Math.abs(delta) < SCROLL_DELTA) return;

        this.#scrollHidden = delta > 0 && current > 0;
        this.#lastScroll = current;
        this.#render();
      });
    }

    #onBodyChange() {
      const classes = Array.from(document.body.classList);
      this.#suppressed = classes.some((name) => name.endsWith('--open') || name === 'overflow-hidden');
      this.#render();
    }

    #render() {
      this.classList.toggle('is-hidden', this.#scrollHidden || this.#suppressed);
    }

    #openSearch() {
      // Mobile search section (sections/mobile-search.liquid) when it's on the page
      if (window.themeMobileSearch && window.matchMedia(MOBILE_MEDIA).matches) {
        window.themeMobileSearch.open();
        return;
      }
      const toggles = Array.from(document.querySelectorAll('search-modal summary, details-modal.header__search summary'));
      const toggle = toggles.find((element) => element.offsetParent !== null) ?? toggles[toggles.length - 1];
      if (toggle) {
        toggle.click();
        return;
      }
      window.location.href = this.dataset.searchUrl || '/search';
    }

    /** Prepares the chat provider: hides native launchers and preloads Sagepilot when idle. */
    #setupChatProvider() {
      const provider = this.dataset.chatProvider;
      const launcher = this.dataset.nativeLauncher || 'hide_mobile';
      const hideCss = (selectors) => {
        const rule = `${selectors} { display: none !important; }`;
        return launcher === 'hide_all' ? rule : `@media ${MOBILE_MEDIA} { ${rule} }`;
      };

      if (provider === 'sagepilot') {
        if (launcher !== 'show') {
          injectStyle(document, 'mobile-bottom-bar-sagepilot', hideCss('#btn-trigger-chat, #chat-bubble, #chat-intro-card'));
        }
        if (this.dataset.sagepilotKey) {
          const preload = () => Sagepilot.load(this.#sagepilotConfig()).catch((error) => {
            console.warn('[mobile-bottom-bar] Sagepilot failed to load', error);
          });
          const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(preload, { timeout: 4000 }) : setTimeout(preload, 2500));
          if (document.readyState === 'complete') idle();
          else window.addEventListener('load', idle, { once: true, signal: this.#controller.signal });
        }
      }

      if (provider === 'shopify_inbox' && launcher !== 'show') {
        // Inbox itself is hidden by the static <style> in the section until our button opens it.
        this.#inboxCss = hideCss(ShopifyInbox.launchers); // older Inbox builds (launcher in a shadow root)
        this.#hideShopifyInboxLauncher(this.#inboxCss);
      }
    }

    #sagepilotConfig() {
      return {
        key: this.dataset.sagepilotKey || '',
        host: this.dataset.sagepilotHost || 'https://app.sagepilot.ai',
        hideLauncher: this.dataset.nativeLauncher === 'hide_all'
      };
    }

    /**
     * Keeps Shopify Inbox's own launcher hidden inside its shadow roots (second layer, so the
     * launcher stays hidden even while Inbox itself is shown for an open chat).
     * Inbox renders late and rebuilds its launcher (after a chat closes, on slow networks, minutes
     * later), so this keeps watching for the whole visit - it used to stop after 30s, which is why
     * the launcher came back at random. Changes are batched, so the cost is one check per burst.
     * @param {string} css
     */
    #hideShopifyInboxLauncher(css) {
      const { signal } = this.#controller;
      const watched = new WeakSet();
      let timer = 0;

      const observer = new MutationObserver(() => {
        if (timer) return;
        timer = window.setTimeout(apply, 120);
      });
      const apply = () => {
        timer = 0;
        ShopifyInbox.hideLauncher(css);
        // Watch inside Inbox too: its rebuilds happen in shadow roots the body observer can't see.
        for (const host of document.querySelectorAll(ShopifyInbox.hosts)) {
          if (!host.shadowRoot || watched.has(host.shadowRoot)) continue;
          watched.add(host.shadowRoot);
          observer.observe(host.shadowRoot, { childList: true, subtree: true });
        }
      };

      apply();
      observer.observe(document.body, { childList: true });
      signal.addEventListener('abort', () => {
        observer.disconnect();
        clearTimeout(timer);
      });
      document.addEventListener('visibilitychange', apply, { signal });
      window.addEventListener('pageshow', apply, { signal }); // back/forward cache restores
    }

    /**
     * Hides Inbox again once the shopper closes the chat (Inbox removes the "open" attribute from
     * <shopify-chat>). If an Inbox build never sets "open", Inbox stays shown after the first chat,
     * but its launcher is still hidden by #hideShopifyInboxLauncher.
     */
    #hideInboxWhenClosed() {
      const root = document.documentElement;
      const host = ShopifyInbox.host();
      if (!host) return;
      this.#inboxCloseObserver?.disconnect();
      let sawOpen = host.hasAttribute('open');
      this.#inboxCloseObserver = new MutationObserver(() => {
        if (host.hasAttribute('open')) {
          sawOpen = true;
          return;
        }
        if (!sawOpen) return;
        root.classList.remove(CHAT_OPEN_CLASS);
        this.#inboxCloseObserver?.disconnect();
      });
      this.#inboxCloseObserver.observe(host, { attributes: true, attributeFilter: ['open'] });
    }

    /** Branded greeting bubble: once per visit, after a delay, hides itself after 10s. */
    #setupGreeting() {
      const greeting = this.querySelector('[data-chat-greeting]');
      if (!greeting) return;

      const seen = () => {
        try {
          return sessionStorage.getItem(GREETING_KEY) === '1';
        } catch {
          return false;
        }
      };
      const markSeen = () => {
        try {
          sessionStorage.setItem(GREETING_KEY, '1');
        } catch {
          // Storage blocked - the greeting may show again on the next page.
        }
      };
      const hide = () => {
        greeting.classList.remove('is-visible');
        setTimeout(() => { greeting.hidden = true; }, 300);
      };

      const { signal } = this.#controller;
      greeting.querySelector('[data-chat-greeting-close]')?.addEventListener('click', () => {
        markSeen();
        hide();
      }, { signal });
      greeting.querySelector('[data-chat-greeting-open]')?.addEventListener('click', () => {
        markSeen();
        hide();
        this.#openChat();
      }, { signal });

      // Theme editor: show every time so changes can be previewed.
      const inEditor = Boolean(window.Shopify?.designMode);
      if (!window.matchMedia(MOBILE_MEDIA).matches) return;
      if (seen() && !inEditor) return;

      const withSound = greeting.dataset.sound === 'true';
      if (withSound) Chime.armUnlock(signal);
      // 0 = stays until the shopper closes it
      const duration = greeting.dataset.duration === undefined ? GREETING_VISIBLE_MS : Number(greeting.dataset.duration);

      const timer = setTimeout(() => {
        if (seen() && !inEditor) return;
        markSeen();
        greeting.hidden = false;
        requestAnimationFrame(() => greeting.classList.add('is-visible'));
        if (withSound) Chime.play(Number(greeting.dataset.soundVolume) || 0.5, greeting.dataset.soundUrl);
        if (duration > 0) setTimeout(hide, duration);
      }, Number(greeting.dataset.delay) || 6000);
      signal.addEventListener('abort', () => clearTimeout(timer));
    }

    async #openChat() {
      const button = this.querySelector('[data-bar-chat]');
      const provider = this.dataset.chatProvider;

      if (provider === 'sagepilot') {
        button?.classList.add('is-loading');
        button?.setAttribute('aria-busy', 'true');
        try {
          await Sagepilot.load(this.#sagepilotConfig());
          if (await Sagepilot.open()) return;
        } catch (error) {
          console.warn('[mobile-bottom-bar] Sagepilot could not open', error);
        } finally {
          button?.classList.remove('is-loading');
          button?.removeAttribute('aria-busy');
        }
        this.#openFallback();
        return;
      }

      if (provider === 'shopify_inbox') {
        if (this.#inboxCss) ShopifyInbox.hideLauncher(this.#inboxCss);
        button?.classList.add('is-loading');
        button?.setAttribute('aria-busy', 'true');
        // Unhide Inbox before asking it to open (the static <style> hides it until this class is set).
        document.documentElement.classList.add(CHAT_OPEN_CLASS);
        try {
          if (await ShopifyInbox.open()) {
            this.#hideInboxWhenClosed();
            return;
          }
          document.documentElement.classList.remove(CHAT_OPEN_CLASS);
        } finally {
          button?.classList.remove('is-loading');
          button?.removeAttribute('aria-busy');
        }
      }

      const customEvent = new CustomEvent(SUPPORT_CHAT_EVENT, { cancelable: true, detail: { provider } });
      if (!document.dispatchEvent(customEvent)) return;

      this.#openFallback();
    }

    #openFallback() {
      if (this.dataset.chatFallback) window.location.href = this.dataset.chatFallback;
    }
  }

  customElements.define('mobile-bottom-bar', MobileBottomBar);
})();
